/**
 * ***********************************************************************
 * Form / report rules for the Fees form's Registration Months field.
 *
 * The per-state term table itself lives in `registrationMonths.js`, which
 * is a mirror of the copies carried by the four DMV bots and by
 * ATC_Flask/fees/handler.py and ATC_Flask/refi_fees/handler.py. Nothing form- or
 * PDF-specific may be added there without putting those copies out of
 * sync, so this module is the frontend-only layer on top of it: what the
 * Fees form prefills, when the field is required, and what the fee
 * summary PDF and output screen print.
 *
 * Fees and Refi-Fees only. Docs and Refi-Docs carry no registration term
 * — no input, no default, no row on their summary — and
 * redux-state/docs/api.js sends no `RegistrationMonths` key at all, so
 * nothing here should be reached from a Docs surface. `titleStatus`
 * ('MSO' | 'T', from fees/contants TITLE_STATUS) is read directly for the
 * same reason: it is the only new/used flag this side of the app uses.
 * ***********************************************************************
 */
import {
  getDefaultRegistrationMonths,
  getSupportedRegistrationTerms,
  DEFAULT_REGISTRATION_MONTHS,
} from './registrationMonths';
import { normalizeState } from './stateRequirements';

const isBlank = (value) =>
  value === null || value === undefined || String(value).trim() === '';

/**
 * Compare two state values for identity.
 *
 * ATC's zipcodeinfo can return an abbreviation or a full name for the same state, so
 * two decodes of the same state can differ as strings. A raw string compare would read
 * 'OH' -> 'Ohio' as a move between states and reset a term the user had deliberately
 * chosen, without the state having changed at all. normalizeState() collapses both
 * forms to the abbreviation and is the same helper stateRequirements.js uses for the
 * identical ambiguity.
 */
const stateKey = (state) => normalizeState(state);

/**
 * The per-state default term for the form's current state / title status /
 * model year, or '' when no state has been resolved yet.
 *
 * '' rather than the bare '12' fallback matters: with no state selected we
 * do not yet know whether the answer is 12, 24, 36 or 48, and prefilling
 * 12 would look like a decision the app had made. Every caller treats ''
 * as "nothing to show / nothing to prefill".
 *
 * @param {object} formData - merged locationInfo + vehicleInfo + transactionInfo
 * @returns {string}
 */
export const getRegistrationMonthsDefault = (formData = {}) => {
  if (isBlank(formData.state)) return '';
  return getDefaultRegistrationMonths(
    formData.state,
    formData.titleStatus,
    formData.modelYear,
    formData.vehicleType
  );
};

/**
 * The options for the Registration Months dropdown.
 *
 * Before a state is known the field still has to render something, and the honest
 * placeholder is the CULS Fees API documented default: a single 12-month option, shown
 * but NOT selected (see getRegistrationMonthsSelection). The moment the ZIP decode
 * resolves a state the list is replaced wholesale by that state's real terms - which is
 * why a state mandating 24 or 36 does not end up with a stray 12 in the list, and why
 * a state offering 12/24/36 gains the longer ones.
 *
 * @param {object} formData - merged locationInfo + vehicleInfo + transactionInfo
 * @returns {string[]} months, as strings, ascending, default first
 */
export const getRegistrationMonthsOptions = (formData = {}) => {
  if (isBlank(formData.state)) return [DEFAULT_REGISTRATION_MONTHS];
  return getSupportedRegistrationTerms(
    formData.state,
    formData.titleStatus,
    formData.modelYear,
    formData.vehicleType
  );
};

/**
 * The value the dropdown should actually display.
 *
 * MUI warns and renders an empty box when a Select's `value` is not among its
 * MenuItems, and there is one render where that can happen: the state changes, the
 * options recompute during render, and the effect that reconciles the stored term has
 * not run yet. Collapsing an unsupported value to '' means that frame shows an empty
 * dropdown rather than a console warning and a phantom selection, and the effect fills
 * it on the very next tick.
 *
 * It also enforces "nothing is selected until a state is": with no state the option
 * list is the placeholder 12, and this returns '' regardless of what redux holds.
 *
 * @param {object} formData
 * @returns {string} a term present in getRegistrationMonthsOptions(formData), or ''
 */
export const getRegistrationMonthsSelection = (formData = {}) => {
  if (isBlank(formData.state)) return '';
  const current = String(formData.registrationMonths ?? '').trim();
  if (!current) return '';
  return getRegistrationMonthsOptions(formData).indexOf(current) === -1 ? '' : current;
};

/**
 * What the stored term should become for the state now selected.
 *
 * Moving between states always resets the term to the new state's default, even when
 * the new state also issues the term the user had picked. A 24 chosen in Pennsylvania
 * is a decision about a Pennsylvania registration; carrying it into Ohio because Ohio
 * happens to sell 24 as well would quote the customer for a term they never asked for
 * in a state they only just arrived in. Resetting makes the field mean the same thing
 * every time a state resolves: this is what the state issues by default, change it if
 * you want something else.
 *
 * `previousState` is the state this field was last reconciled against. Two cases are
 * deliberately NOT treated as a change, because neither is the user moving between
 * states:
 *   - undefined, the first reconcile after a mount. The `forms` slice survives
 *     navigation and the multi-step flow mounts this form more than once, so a term
 *     from an earlier visit is adopted if the state still issues it.
 *   - blank, the ZIP decode resolving a state for the first time.
 *
 * A term the current state will not issue is always replaced, whatever moved it out of
 * range - a title status flipping Michigan from 12/24 to 12-only, or a model year
 * dropping Texas off the 24-month gate. That safety net is why only genuine state
 * changes need the history above.
 *
 * Returns null when nothing needs to change, so the caller can skip the dispatch
 * instead of re-rendering the form on every unrelated keystroke.
 *
 * @param {object} formData
 * @param {string} [previousState] - the state last reconciled against
 * @returns {string|null} the term to store, or null to leave it alone
 */
export const reconcileRegistrationMonths = (formData = {}, previousState) => {
  if (isBlank(formData.state)) return null;

  const options = getRegistrationMonthsOptions(formData);
  const current = String(formData.registrationMonths ?? '').trim();

  const movedBetweenStates = !isBlank(previousState)
    && stateKey(previousState) !== stateKey(formData.state);

  // Comparing against options[0] rather than returning it unconditionally keeps a
  // state change that lands on the same default (PA -> MD, both 12) from dispatching a
  // no-op and re-rendering the form.
  if (movedBetweenStates) return current === options[0] ? null : options[0];

  if (options.indexOf(current) !== -1) return null;
  return options[0];
};

/**
 * The registration term that actually reaches ATC: what the user entered,
 * or the per-state default `registrationmonths` in redux-state/fees/api.js
 * falls back to when the field was left empty.
 *
 * This is what the fee summary PDF and the output screen print, so the
 * number on the paperwork is the number the quote was built from — a blank
 * row used to leave the reader to guess, and the guess ("12") was wrong in
 * every state that mandates a longer term.
 *
 * @param {object} formData
 * @returns {string}
 */
export const getEffectiveRegistrationMonths = (formData = {}) => {
  if (!isBlank(formData.registrationMonths)) {
    return String(formData.registrationMonths).trim();
  }
  return getRegistrationMonthsDefault(formData);
};

/**
 * Registration Months is required as soon as a state is known.
 *
 * The Fees form prefills the field from the per-state table the moment the
 * ZIP decode resolves a state, so in normal use this asterisk never blocks
 * anyone — it exists to stop a submission going out with the term cleared,
 * which would silently fall back to the state default rather than to
 * whatever the user meant to type.
 *
 * @param {string} state
 * @returns {boolean}
 */
export const isRegistrationMonthsRequired = (state) => !isBlank(state);

/**
 * Validation helper for the Fees branch of FormValidation. Writes
 * `"Registration Months is required for <State>"` when a state has been
 * selected and the field is empty.
 *
 * Never overwrites an error already set by the static required-field list,
 * matching applyStateSpecRequirements in stateRequirements.js.
 */
export const applyRegistrationMonthsRequirement = (formData = {}, errors = {}) => {
  if (errors.registrationMonths) return errors;
  if (!isRegistrationMonthsRequired(formData.state)) return errors;
  if (isBlank(formData.registrationMonths)) {
    errors.registrationMonths = `Registration Months is required for ${formData.state}`;
  }
  return errors;
};

export default getEffectiveRegistrationMonths;
